import React, { useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { CreditCard, Banknote, Building2, Tag } from 'lucide-react'
import Modal from './Modal'
import { cn } from '../lib/utils'

// R1.5 — Record-payment modal. Card / cash / EFT / Discount, full or partial.
//
// Two modes:
//  • INVOICE mode  (pass `invoice`) — records a payment against one invoice,
//    defaults to the outstanding balance so "pay it off" is one click.
//  • ACCOUNT mode  (pass `account`) — takes a payment at the account level even
//    when it isn't tied to an invoice yet. By default it settles the oldest open
//    invoices first and banks the remainder as credit; untick "Allocate…" to take
//    it purely as an unallocated deposit/credit (e.g. a whitening deposit).
//
// Every payment now carries a DATE the money was received (defaults to today)
// because deposits are often taken before the work / invoice exists.

const METHODS = [
  { value: 'card',     label: 'Card',     icon: CreditCard, hint: 'Yoco / Zapper tap' },
  { value: 'cash',     label: 'Cash',     icon: Banknote,   hint: 'Counted at reception' },
  { value: 'eft',      label: 'EFT',      icon: Building2,  hint: 'Bank transfer' },
  { value: 'discount', label: 'Discount', icon: Tag,        hint: 'Practice write-down' },
]

const todayISO = () => {
  const d = new Date()
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10)
}
// Floor the back-date picker 5 years back — matches the server guard; stops fat-fingered years.
const minISO = () => {
  const d = new Date(); d.setFullYear(d.getFullYear() - 5)
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10)
}

export default function PaymentModal({ open, onClose, invoice = null, account = null, onSaved = null }) {
  const isAccount = !!(account && account.id) && !(invoice && invoice.id)
  const balance = isAccount
    ? Math.max(0, Number(account?.outstanding || 0))
    : Math.max(0, Number(invoice?.balance || 0))

  const [amount, setAmount]       = useState('')
  const [method, setMethod]       = useState('card')
  const [reference, setReference] = useState('')
  const [notes, setNotes]         = useState('')
  const [receivedAt, setReceivedAt] = useState(todayISO())
  const [allocate, setAllocate]   = useState(true) // account mode only
  const [saving, setSaving]       = useState(false)
  const [amountError, setAmountError] = useState('')
  const amountRef = useRef(null)

  useEffect(() => {
    if (!open) return
    // INVOICE payments: DO NOT auto-populate the amount — reception must type it in manually as an
    // accuracy double-check, so a wrong/partial amount can't be banked on autopilot (Paul 2026-06-26).
    // Account-level payments keep the balance prefill (those are usually a full settlement).
    setAmount(isAccount && balance > 0 ? balance.toFixed(2) : '')
    setMethod('card'); setReference(''); setNotes(''); setReceivedAt(todayISO())
    setAllocate(true); setSaving(false); setAmountError('')
    setTimeout(() => { amountRef.current?.focus(); amountRef.current?.select() }, 0)
  }, [open, balance])

  const overpay = useMemo(() => {
    const a = parseFloat(amount)
    return balance > 0 && a > balance ? a - balance : 0
  }, [amount, balance])

  const submit = () => {
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) {
      setAmountError('Enter an amount greater than R0')
      amountRef.current?.focus()
      return
    }
    setAmountError('')
    setSaving(true)

    const done = (defaultMsg) => ({
      preserveScroll: true,
      onSuccess: (page) => {
        toast.success(page?.props?.flash?.notice || defaultMsg)
        // Flag the owning patient's page as stale so its money cards re-sync silently on return.
        onSaved?.()
        onClose?.()
      },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not record'),
      onFinish: () => setSaving(false),
    })

    if (isAccount) {
      // Allocate → settle oldest invoices first, remainder to credit.
      // Don't allocate → bank the whole amount as an unallocated account credit (deposit).
      const url = allocate
        ? `/accounts/${account.id}/receive_payment`
        : `/accounts/${account.id}/deposit`
      router.post(url, {
        amount: amt, method, received_at: receivedAt,
        reference: reference || null, notes: notes || null,
      }, done('Payment recorded'))
    } else {
      router.post(`/invoices/${invoice.id}/payments`, {
        amount: amt, method, received_at: receivedAt,
        reference: reference || null, notes: notes || null,
      }, done('Payment recorded'))
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isAccount ? 'Add payment to account' : 'Record payment'}
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-medium text-brand-muted hover:bg-brand-surface/45 hover:text-brand-ink">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={saving}
            className="rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            {isAccount ? 'Add payment' : 'Record payment'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {isAccount ? (
          <div className="rounded-xl border border-brand-border bg-brand-surface/40 px-3 py-2 text-sm text-brand-ink">
            {balance > 0
              ? <>Account outstanding: <strong>R{balance.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</strong></>
              : <>No outstanding invoices — this will be banked as account credit.</>}
          </div>
        ) : balance > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Outstanding balance: <strong>R{balance.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</strong>
          </div>
        ) : (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Invoice already paid in full. You can still record a refund/credit if needed.
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Method</label>
          <div className="grid grid-cols-4 gap-2">
            {METHODS.map((m) => {
              const Icon = m.icon
              const active = method === m.value
              return (
                <button key={m.value} type="button" onClick={() => setMethod(m.value)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-sm font-medium transition-colors',
                    active
                      ? 'border-brand-primary bg-brand-primary/5 text-brand-primary ring-2 ring-brand-primary/30'
                      : 'border-brand-border bg-white text-brand-ink hover:bg-brand-surface'
                  )}>
                  <Icon size={20} />
                  {m.label}
                  <span className="text-[10px] leading-tight text-brand-muted">{m.hint}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Amount (R)</label>
            <input ref={amountRef} type="number" step="0.01" value={amount}
              onChange={(e) => { setAmount(e.target.value); if (amountError) setAmountError('') }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
              className={`w-full rounded-2xl border bg-white px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:ring-4 ${amountError ? 'border-red-400 focus:ring-red-200' : 'border-brand-accent/80 focus:border-brand-primary focus:ring-brand-accent/45'}`} />
            {amountError && <p className="mt-1 text-xs text-red-600">{amountError}</p>}
            {overpay > 0 && (
              <p className="mt-1 text-xs text-amber-700">R{overpay.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} over the balance — recorded as a patient credit.</p>
            )}
            {balance > 0 && (
              <button type="button" onClick={() => { setAmount(balance.toFixed(2)); setAmountError('') }}
                className="mt-1 text-xs text-brand-primary hover:underline">
                Pay full balance (R{balance.toFixed(2)})
              </button>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Date received</label>
            <input type="date" value={receivedAt} max={todayISO()} min={minISO()}
              onChange={(e) => setReceivedAt(e.target.value)}
              className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
          </div>
        </div>

        {method === 'eft' && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">EFT reference</label>
            <input type="text" value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. POP-1234"
              className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
          </div>
        )}

        {isAccount && (
          <label className="flex items-start gap-2.5 rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm text-brand-ink">
            <input type="checkbox" checked={allocate} onChange={(e) => setAllocate(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary" />
            <span>
              <span className="font-medium">Allocate to outstanding invoices</span>
              <span className="block text-xs text-brand-muted">
                {allocate
                  ? 'Settles the oldest open invoices first; any remainder is banked as account credit.'
                  : 'Take it regardless of an invoice — the full amount is banked as an unallocated account credit (e.g. a deposit).'}
              </span>
            </span>
          </label>
        )}

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Notes (optional)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
        </div>
      </div>
    </Modal>
  )
}
